import { supabase } from '../../supabase';
import { Paper, PaperAuthor } from '@/types/paper';
import { mapToPaper, deduplicateAuthors } from '../paper-mappers';
import { toast } from 'sonner';
import { adminLog, adminError } from '@/lib/utils/admin-logger';
import { expandCategories } from '@/lib/constants/arxivCategories';
import { fetchUsersWithCache } from './userCache';

export interface PaperFetchOptions {
  offset?: number;
  limit?: number;
  sortBy?: string;
  search?: string;
  categories?: string[];
  keywords?: string[];
  minRating?: number;
}

// User cache for reducing redundant author fetches across batches
// User cache is now in shared userCache.ts module

/**
 * Get all papers from the database with optional pagination and filtering
 * @param options Optional pagination and filtering options
 * @param signal Optional AbortSignal for request cancellation
 * @returns Array of Paper objects and hasMore flag
 */
export async function getAllPapers(options?: PaperFetchOptions, signal?: AbortSignal): Promise<{ papers: Paper[], hasMore: boolean }> {
  try {
    // Check for abort signal
    if (signal?.aborted) {
      throw new DOMException('Request was aborted', 'AbortError');
    }
    
    const {
      offset = 0,
      limit = 20,
      sortBy = 'updated_at',
      search,
      categories,
      keywords,
      minRating,
    } = options || {};
    
    adminLog(`Fetching papers (offset ${offset}, limit ${limit}, sort: ${sortBy})`);

    // Use the database function for search with author names when there's a search term
    if (search && search.trim()) {
      adminLog(`🔍 Using search function for query: "${search}"`);
      
      // Expand hierarchical categories for search (instant - uses static map)
      const expandedCategories = categories && categories.length > 0 ? expandCategories(categories) : null;
      
      const { data: functionResults, error: functionError } = await supabase
        .rpc('search_papers_with_authors', {
          search_term: search.trim(),
          category_filter: expandedCategories,
          keyword_filter: keywords && keywords.length > 0 ? keywords : null,
          sort_option: sortBy === 'updated_at' ? 'date' : sortBy,
          page_offset: offset,
          page_limit: limit,
          min_rating: minRating || null
        }).abortSignal(signal);

      adminLog(`📡 RPC call completed. Error: ${functionError ? JSON.stringify(functionError) : 'none'}, Results count: ${functionResults?.length || 0}`);

      if (functionError) {
        // If the request was aborted, throw AbortError so callers handle it silently
        if (signal?.aborted) {
          throw new DOMException('Request was aborted', 'AbortError');
        }
        adminError('Error fetching papers with search function:', functionError);
        toast.error('Failed to load papers', { description: functionError.message });
        return { papers: [], hasMore: false };
      }

      if (!functionResults || functionResults.length === 0) {
        adminLog('❌ No papers found matching the search criteria');
        return { papers: [], hasMore: false };
      }

      adminLog(`✅ Search function returned ${functionResults.length} papers`);

      // Use pre-computed counts from the RPC result directly (no follow-up queries needed)
      const paperRows = functionResults.map(paper => ({
        ...paper,
        paper_versions: [],
      }));

      // Process the results using the same logic as the regular query
      const candidateAuthorIds = new Set<string>();
      paperRows.forEach(paper => {
        if (paper.author_id) candidateAuthorIds.add(paper.author_id);
        if (Array.isArray(paper.authors)) {
          paper.authors.forEach((author: any) => {
            if (author.isPlatformUser && author.id) candidateAuthorIds.add(author.id);
          });
        }
      });
      const authorIds = Array.from(candidateAuthorIds);
      
      // Check abort before user cache fetch
      if (signal?.aborted) {
        throw new DOMException('Request was aborted', 'AbortError');
      }
      const usersMap = await fetchUsersWithCache(authorIds, signal);

      // Map papers with updated author info
      const mappedPapers = paperRows.map(paperRow => {
        const joinedVersions = (paperRow.paper_versions || []) as any[];
        let mappedPaper = mapToPaper(paperRow, joinedVersions);

        mappedPaper.reviewCount = paperRow.review_count ?? 0;
        mappedPaper.commentCount = paperRow.comment_count ?? 0;
        mappedPaper.averageRating = paperRow.average_rating != null ? Number(paperRow.average_rating) : undefined;

        if (mappedPaper.authors && mappedPaper.authors.length > 0) {
          mappedPaper.authors = mappedPaper.authors.map((author) => {
            if (author.isPlatformUser && usersMap.has(author.id)) {
              const user = usersMap.get(author.id);
              return {
                ...author,
                id: user.id,
                name: user.name,
                institution: user.institution || undefined,
                role: user.role || undefined,
                isPlatformUser: true
              } as PaperAuthor;
            }
            return author;
          });
          mappedPaper.authors = deduplicateAuthors(mappedPaper.authors);
        }
        return mappedPaper;
      });

      const hasMore = functionResults.length === limit;
      adminLog(`🎯 Successfully returned ${mappedPapers.length} mapped papers with search. HasMore: ${hasMore}`);
      return { papers: mappedPapers, hasMore };
    }

    // Handle non-search filters using direct query for better performance
    adminLog(`📄 Using direct papers query (no search term)`);
    // IMPORTANT: Only fetch public papers for general browsing
    let query = supabase
      .from('papers')
      .select(`
        id, title, abstract, author_id, category, keywords,
        current_version, published_at, updated_at, views,
        external_url, created_at, authors, license,
        license_compliance_status, average_rating,
        review_count, comment_count, visibility,
        ai_review_count, last_ai_review_at, made_public_at,
        license_fetched_at, license_validation_error
      `)
      .eq('visibility', 'public');

    // Apply category filter if provided - handle hierarchical categories
    if (categories && categories.length > 0) {
      // Expand hierarchical categories to specific subcategories (instant - uses static map)
      const expandedCategories = expandCategories(categories);
      if (expandedCategories.length > 0) {
        query = query.in('category', expandedCategories);
      }
    }

    // Apply keyword filter if provided  
    if (keywords && keywords.length > 0) {
      query = query.contains('keywords', keywords);
    }

    // Apply minimum rating filter
    if (minRating) {
      query = query.gte('average_rating', minRating);
    }

    // Apply sorting
    if (sortBy === 'date' || sortBy === 'updated_at') {
      query = query.order('updated_at', { ascending: false });
    } else if (sortBy === 'views') {
      query = query.order('views', { ascending: false });
    } else if (sortBy === 'rating') {
      query = query.order('average_rating', { ascending: false, nullsFirst: false });
    }

    // Apply pagination with offset and limit
    query = query.range(offset, offset + limit - 1);

    // Execute the query (don't fetch count for performance)
    const { data: paperRows, error } = await query.abortSignal(signal);

    if (error) {
      // If the request was aborted, throw AbortError so callers handle it silently
      if (signal?.aborted) {
        throw new DOMException('Request was aborted', 'AbortError');
      }
      adminError('Error fetching papers & versions:', error);
      toast.error('Failed to load papers', { description: error.message });
      return { papers: [], hasMore: false };
    }
    
    if (!paperRows || paperRows.length === 0) {
      adminLog('No papers found in the database');
      return { papers: [], hasMore: false };
    }

    adminLog(`Fetched ${paperRows.length} papers including versions.`);
    
    // Gather author IDs synchronously
    const candidateAuthorIds = new Set<string>();
    paperRows.forEach(paper => {
      if (paper.author_id) candidateAuthorIds.add(paper.author_id);
      if (Array.isArray(paper.authors)) {
        paper.authors.forEach((author: any) => {
          if (author.isPlatformUser && author.id) candidateAuthorIds.add(author.id);
        });
      }
    });
    const authorIds = Array.from(candidateAuthorIds);
    
    // Check abort before user cache fetch
    if (signal?.aborted) {
      throw new DOMException('Request was aborted', 'AbortError');
    }
    const usersMap = await fetchUsersWithCache(authorIds, signal);

    // Map papers and versions to Paper[]
    const mappedPapers = paperRows.map(paperRow => {
      // No version data needed for Explore cards
      let mappedPaper = mapToPaper(paperRow, []);

      // Read pre-computed counts directly from the papers table
      mappedPaper.reviewCount = paperRow.review_count ?? 0;
      mappedPaper.commentCount = paperRow.comment_count ?? 0;
      mappedPaper.averageRating = paperRow.average_rating != null ? Number(paperRow.average_rating) : undefined;

      // Update author info with up-to-date platform data, preserving order
      if (mappedPaper.authors && mappedPaper.authors.length > 0) {
        mappedPaper.authors = mappedPaper.authors.map((author) => {
          if (author.isPlatformUser && usersMap.has(author.id)) {
            const user = usersMap.get(author.id);
            return {
              ...author,
              id: user.id, // ensure correct ID
              name: user.name,
              institution: user.institution || undefined,
              role: user.role || undefined,
              isPlatformUser: true
            } as PaperAuthor;
          }
          return author;
        });
        mappedPaper.authors = deduplicateAuthors(mappedPaper.authors);
      }
      return mappedPaper;
    });

    const hasMore = paperRows.length === limit;
    adminLog(`Successfully returned ${mappedPapers.length} mapped papers. HasMore: ${hasMore}`);
    return { papers: mappedPapers, hasMore };
  } catch (error) {
    // Don't log or toast for aborted requests
    if (error && typeof error === 'object' && 'name' in error && (error as any).name === 'AbortError') {
      throw error;
    }
    
    adminError('Unexpected error in getAllPapers:', error);
    toast.error('An unexpected error occurred', {
      description: 'Could not fetch papers due to a system error.'
    });
    return { papers: [], hasMore: false };
  }
}